import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCreateDataPoint } from '../DataPoint';
import { useCreateDataPointGroup } from '../DataPointGroup';
import { DataPointGroupType } from '../generated/graphql';
import { recurringMonetaryAmountDataPointValueInput } from '../util';
import { useCreateCashAccount, useUpdateCashAccount } from './mutations';
import {
  CreateCashAccountServiceInput,
  UpdateCashAccountRecurringContributionServiceInput,
  UpdateCashAccountServiceInput,
  UpdateCashAccountValueServiceInput,
} from './types';
import { useCreateFileAttachment } from '../FileAttachment';
import { ttDetailsKeys } from '../TtDetails';
import { erDetailsKeys } from '../ErDetails';
import { srDetailsKeys } from '../SrDetails';
import { cashAccountKeys } from './queryKeys';
import { scorecardKeys } from '../Scorecard';
import { ltDetailsKeys } from '../LtDetails';

export const useUpdateCashAccountValueService = () => {
  const { createDataPoint } = useCreateDataPoint();

  const updateCashAccountValueService = useMutation({
    mutationKey: ['updateCashAccountValueService'],
    mutationFn: async (input: UpdateCashAccountValueServiceInput) => {
      await createDataPoint.mutateAsync({
        dataPoint: {
          dateTime: new Date().toISOString(),
          data: {
            monetaryAmountValue: {
              value: {
                currencyCode: 'USD',
                value: input.valueInCents,
              },
            },
          },
          groupID: input.valueDataPointGroupID,
        },
        tenantID: input.householdID,
      });
    },
  });

  return { updateCashAccountValueService };
};

export const useUpdateCashAccountRecurringContributionService = () => {
  const { createDataPoint } = useCreateDataPoint();

  const updateCashAccountRecurringContributionService = useMutation({
    mutationKey: ['updateCashAccountRecurringContributionService'],
    mutationFn: async (input: UpdateCashAccountRecurringContributionServiceInput) => {
      await createDataPoint.mutateAsync({
        dataPoint: {
          dateTime: new Date().toISOString(),
          data: {
            recurringMonetaryAmountValue: input.recurringContributionValueInput,
          },
          groupID: input.recurringContributionDataPointGroupID,
        },
        tenantID: input.householdID,
      });
    },
  });

  return { updateCashAccountRecurringContributionService };
};

export const useCreateCashAccountService = () => {
  const queryClient = useQueryClient();
  const { createDataPointGroup } = useCreateDataPointGroup();
  const { createCashAccount } = useCreateCashAccount();
  const { updateCashAccountRecurringContributionService } = useUpdateCashAccountRecurringContributionService();
  const { updateCashAccountValueService } = useUpdateCashAccountValueService();

  const createCashAccountService = useMutation({
    mutationKey: ['createCashAccountService'],
    mutationFn: async ({
      createCashAccountInput: { householdID, cashAccount, linkSwitch },
      ...input
    }: CreateCashAccountServiceInput) => {
      const { dataPointGroup: valueDataPointGroup } = await createDataPointGroup.mutateAsync({
        tenantID: householdID,
        dataPointGroup: { groupType: DataPointGroupType.MonetaryAmount },
      });
      await updateCashAccountValueService.mutateAsync({
        householdID,
        valueInCents: input.valueInCents,
        valueDataPointGroupID: valueDataPointGroup.id,
      });

      let recurringContributionDataPointGroupID: string | undefined;
      if (input.recurringContributionFrequency && !isNaN(Number(input.recurringContributionInCents))) {
        const { dataPointGroup } = await createDataPointGroup.mutateAsync({
          tenantID: householdID,
          dataPointGroup: {
            groupType: DataPointGroupType.RecurringMonetaryAmount,
          },
        });
        recurringContributionDataPointGroupID = dataPointGroup.id;
        await updateCashAccountRecurringContributionService.mutateAsync({
          householdID,
          recurringContributionDataPointGroupID: dataPointGroup.id,
          recurringContributionValueInput: recurringMonetaryAmountDataPointValueInput({
            frequency: input.recurringContributionFrequency,
            valueInCents: Number(input.recurringContributionInCents),
          }),
        });
      }

      await createCashAccount.mutateAsync({
        householdID,
        linkSwitch,
        cashAccount: {
          ...cashAccount,
          valueDataPointGroup: valueDataPointGroup.id,
          recurringContributionDataPointGroup: recurringContributionDataPointGroupID,
        },
      });
    },
    onSuccess: (_, { createCashAccountInput: { householdID } }) => {
      queryClient.invalidateQueries(ttDetailsKeys.ttDetails({ householdID }));
      queryClient.invalidateQueries(erDetailsKeys.erDetails({ householdID }));
      queryClient.invalidateQueries(srDetailsKeys.srDetails({ householdID }));
      queryClient.invalidateQueries(cashAccountKeys.cashAccounts(householdID));
      queryClient.invalidateQueries(scorecardKeys.scorecard(householdID));
      queryClient.invalidateQueries(ltDetailsKeys.ltDetails({ householdID }));
    },
  });

  return { createCashAccountService };
};

export const useUpdateCashAccountService = () => {
  const queryClient = useQueryClient();
  const { updateCashAccount } = useUpdateCashAccount();
  const { updateCashAccountRecurringContributionService } = useUpdateCashAccountRecurringContributionService();
  const { updateCashAccountValueService } = useUpdateCashAccountValueService();
  const { createFileAttachment } = useCreateFileAttachment();

  const updateCashAccountService = useMutation({
    mutationKey: ['updateCashAccountService'],
    mutationFn: async (input: UpdateCashAccountServiceInput) => {
      const { cashAccount } = await updateCashAccount.mutateAsync(input.updateCashAccountInput);

      await Promise.all(
        (input.filesToAttach || []).map((pendingFile) =>
          createFileAttachment.mutateAsync({
            tenantID: cashAccount.householdID,
            fileAttachment: { fileID: pendingFile.id, itemID: cashAccount.id },
          })
        )
      );

      if (!isNaN(Number(input.estimatedValueInCents))) {
        await updateCashAccountValueService.mutateAsync({
          householdID: cashAccount.householdID,
          valueDataPointGroupID: cashAccount.value.id,
          valueInCents: Number(input.estimatedValueInCents),
        });
      }

      if (input.recurringContributionFrequency && !isNaN(Number(input.recurringContributionInCents))) {
        await updateCashAccountRecurringContributionService.mutateAsync({
          householdID: cashAccount.householdID,
          recurringContributionDataPointGroupID: cashAccount.recurringContribution.id,
          recurringContributionValueInput: recurringMonetaryAmountDataPointValueInput({
            frequency: input.recurringContributionFrequency,
            valueInCents: input.recurringContributionInCents,
          }),
        });
      }
    },
    onSuccess: (_, { updateCashAccountInput: { householdID } }) => {
      queryClient.invalidateQueries(ttDetailsKeys.ttDetails({ householdID }));
      queryClient.invalidateQueries(erDetailsKeys.erDetails({ householdID }));
      queryClient.invalidateQueries(srDetailsKeys.srDetails({ householdID }));
      queryClient.invalidateQueries(cashAccountKeys.cashAccounts(householdID));
      queryClient.invalidateQueries(scorecardKeys.scorecard(householdID));
    },
  });

  return { updateCashAccountService };
};
